import * as React from 'react';
import { useDispatch, useStore } from 'react-redux';
import { getDocumentHelper } from '../../../../actions/utility-actions';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { StickyActionBar } from '../../../../components/sticky-action-bar/StickyActionBar';
import { useContextMenu } from '../../../../hooks/use-context-menu';
import { useScrollMemory } from '../../../../hooks/use-scroll-memory';
import { useSearchParams } from '../../../../hooks/use-search-params';
import { AppliedFilterBarOpt } from '../../../../models/utilities/filter-bar';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import * as transportActions from '../../../../transportation/actions';
import { StatusCode } from '../../../../utils/api';
import { Routes, ScrollStatePaths } from '../../../../utils/app-constants';
import { navigateToPage } from '../../../../utils/history';
import { DeliveryMethod } from '../../../../transportation/models/enums/delivery-method';
import { ShipmentType } from '../../../../transportation/models/enums/shipment-type';
import { DeliveryPlanningFilters } from '../../../../transportation/models/filters/delivery-planning-filters';
import { StopFileDocument } from '../../../../transportation/models/planning/stop-file-document';
import { UnplannedRouteGroup } from '../../../../transportation/models/planning/unplanned-route-group';
import { RunningTotal } from '../../../../transportation/models/shared/running-total';
import { getDeliveryPlanningFilters } from '../../../../transportation/selectors';
import { TransportationSvc } from '../../../../transportation/service';
import { RoutingRightClickMenu } from './RoutingRightClickMenu';
import { RoutingRouteCreate } from './RoutingRouteCreate';
import UnplannedRouteRow from './UnplannedRouteRow';

const IGNORE_UNMOUNT_SELECTABLE = false;

const ContextMenu = ({ menu, onShow, onHide }: any) => {
    const { xPos, yPos, showMenu } = useContextMenu(menu, onShow, onHide);
    return showMenu
        ? <div
            className="right-click menu-container"
            style={{
                top: yPos,
                left: xPos,
                opacity: 1
            }}>
            {menu}
        </div>
        : null;
};

export const RoutingRouteList = () => {

    const store                                 = useStore();
    const dispatch                              = useDispatch();
    const [service]                             = React.useState(new TransportationSvc());
    const [filterDate, setFilterDate]           = React.useState<string|undefined>(undefined);
    const [selecteds, setSelecteds]             = React.useState<UnplannedRouteGroup[]>([]);
    const [showInfoFlyout, setShowInfoFlyout]   = React.useState(false);
    const [entityType, setEntityType]           = React.useState(ShipmentType.Truck);
    const [selectedTotal, setSelectedTotal]     = React.useState<RunningTotal>(new RunningTotal());
    const [isMenuShown, setIsMenuShown]         = React.useState(false);
       
    const [filterParams, setFilterParams]   = React.useState<DeliveryPlanningFilters>(() => {
        return getDeliveryPlanningFilters(store.getState());
    });
    
    const [deliveries, setDeliveries]       = React.useState(() => {
        const initial = new PagedApiResult<UnplannedRouteGroup>();
        initial.pagination = filterParams.paginationInfo;
        initial.pagination.reusePaging = true;
        return initial;
    });
    
    const scrollTo = useScrollMemory(
        ScrollStatePaths.PLANNING_DELIVERY, 
        Routes.ROUTING_ROUTING
    );

    useSearchParams(
        Routes.ROUTING_ROUTING_DELIVERIES,        
        transportActions.updateDeliveryPlanningFilters(filterParams),
        transportActions.clearDeliveryPlanningFilters()
    );

    /**
     * Start making a truck or a container of the selected rows
     * @param type What to make
     */
    const startCreate = (type : ShipmentType) => {
        setEntityType(type);
        setShowInfoFlyout(true);
    };

    /**
     * The actions of the right click menu, which is of the rows that are selected
     */
    const getMenuContext = () => {
        return <RoutingRightClickMenu
            selectedCount={ selecteds.length }
            exportStopFile={ getStopFile }
            importStopFile={ () => navigateToPage(Routes.ROUTING_ROUTING_IMPORT) }
            createTruck={ () => startCreate(ShipmentType.Truck) }
            createContainer={ () => startCreate(ShipmentType.Container) }
            setGroupings={ () => selecteds.length === 1 && goToDetails(selecteds[0]) }
            heightOffset={ selecteds.length === 0 ? 150 : selecteds.length === 1 ? 330 : 260 }
        />;
    };

    /**
     * Download the stop file for the given date
     */
    const getStopFile = async () => {
        const hasBadAddresses = deliveries.results.some((itm: UnplannedRouteGroup) => 
            itm.isBadAddress
        );

        if (hasBadAddresses) {
            const result = await ResultDialog.show({
                title: 'Bad Addresses Found',
                message: 'Your export list contains bad addresses, these will be excluded from the stop file.  Are you sure you want to continue?'
            });

            if (result) {
                makeDocumentRequest();
            }
        }
        else {
            makeDocumentRequest();
        }
    };

    /**
     * Send the request for the stop file
     */
    const makeDocumentRequest = () => {
        const req = new StopFileDocument(filterParams, selecteds);
        dispatch(getDocumentHelper(req));
    };

    /**
     * Navigate to the order grouping page
     * @param itm The row that was clicked
     */
    const goToDetails = (itm: UnplannedRouteGroup) => {
        
        let subPageUrl = '';
        subPageUrl = `${ Routes.ROUTING_ROUTING_DELIVERIES }/${ itm.dealerId }/${ itm.shipToAddressId }`;

        if (filterDate) {
            subPageUrl += `/${ filterDate }`;
        }


        let division = '';

        filterParams.appliedFilters.forEach((itm: AppliedFilterBarOpt) => {
            if (itm.qsName === 'division') {
                if (!division || division.length === 0) {
                    division = itm.qsValue.toString();
                }
                else {
                    division += ',' + itm.qsValue.toString();
                }
            }
        });

        const searchParams = new URLSearchParams();
        if (division && division.length > 0) {
            searchParams.append('division', division);
        }

        var args = searchParams.toString();
        if (args && args.length > 0) {
            subPageUrl += '?' + args;
        }        

        navigateToPage(subPageUrl);
    };

    /**
     * OnSearch handler
     * @param filters The list of filters to apply to the search
     */
    const onSearch = async (filters: DeliveryPlanningFilters) => {
        const response = await service.searchUnplannedRoutes(filters);
        if (response.status === StatusCode.Success) {
            setDeliveries(response.data);
            scrollTo();
        }

        const cutoffFilter = filters.appliedFilters.find((fItm : AppliedFilterBarOpt) => {
            return fItm.qsName === 'maxCutoff';
        });

        if (cutoffFilter) {
            setFilterDate(cutoffFilter.qsValue);
        }
        else {
            setFilterDate(undefined);
        }

        setFilterParams(filters);
        clearSelections();
    };

    const onParamsUpdate = (itm : any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        const tmp = new DeliveryPlanningFilters();
        setFilterParams(tmp);
    };

    const getAddressUrl = (d : UnplannedRouteGroup) => {
        return `/support/dealers/${ d.dealerId }/addresses/${ d.shipToAddressId }`;
    };

    /**
     * Handler for when the user is done drag selecting items
     * @param evt The draggable event with selected items
     */   
    const onDragComplete = (evt : any) => {

        if (IGNORE_UNMOUNT_SELECTABLE) {
            return;
        }

        const tmpDeliveries = {...deliveries};

        tmpDeliveries.results.forEach((itm : UnplannedRouteGroup) => {
            itm.isChecked = false;
        });

        const tmpList : UnplannedRouteGroup[] = [];
        for (let j = 0; j < evt.length; j++) {
            const evtItem = evt[j];

            for (let i = 0; i < tmpDeliveries.results.length; i++) {
                if (tmpDeliveries.results[i].tmpClientId === evtItem.props.delivery.tmpClientId) { 
                    tmpDeliveries.results[i].isChecked = true;
                    tmpList.push(tmpDeliveries.results[i]);
                    break;
                }
            }
        }
 
        setSelecteds(tmpList);
        calculateTotals(tmpList);
        setDeliveries(tmpDeliveries);
    };

    /**
     * Nothing is selected, as when the list is fetched again
     */
    const clearSelections = () => {
        setSelecteds([]);
        setSelectedTotal(new RunningTotal());
    };

    const calculateTotals = (tmpList : UnplannedRouteGroup[]) => {

        const tmpTotals = new RunningTotal();

        tmpList.forEach((itm : UnplannedRouteGroup) => {

            tmpTotals.units += itm.units;
            tmpTotals.weight += itm.weight;
            tmpTotals.singles += itm.singles;
            tmpTotals.sections += itm.sections;
            tmpTotals.track += itm.track;
            tmpTotals.hardware += itm.hardware;
            tmpTotals.feet += itm.feet;
            
        });

        setSelectedTotal(tmpTotals);        
    };

    /**
     * Handle a successful planning create
     */
    const onCreateSuccess = async () => {
        onSearch(filterParams);
        setShowInfoFlyout(false);
    };
    
    /**
     * Close the flyout
     */
    const onCreateClose = () => {
        setShowInfoFlyout(false);
    };

    return <React.Fragment>
                <PagedList
                    noResultsMessage="No Unplanned Deliveries found"
                    searchCallback={ onSearch }
                    data={ deliveries }
                    hidePager={ true }
                    selectableClass=""
                    rowClass="planning-route-header-row"
                    filterProps={ filterParams }
                    onFilterUpdate={ onParamsUpdate }
                    onReset={ onParamsReset }
                    reduxDispatch={ dispatch }
                    onDragComplete={ onDragComplete }
                    enabledDrag={ !isMenuShown }
                    rowRenderer={(itm: UnplannedRouteGroup, extraProps: any) => 
                        <UnplannedRouteRow
                            key={`upprr-${ itm.tmpClientId }`}
                            readonly={ false }
                            method={ DeliveryMethod.CompanyTruck }
                            addressUrl={ getAddressUrl(itm) }
                            delivery={ itm }  
                            { ...extraProps }
                        />
                    }
                />  

                <ContextMenu
                    menu={ getMenuContext() }
                    onShow={ () => setIsMenuShown(true) }
                    onHide={ () => setIsMenuShown(false) } />

                <StickyActionBar 
                    classes="planning-running-totals"
                    showBar={ true }>
                    <div className="running-total-bar">
                        <p className="row-label">Selected Totals</p>
                        <div>
                            <p className="rollup-metric">
                                <span>Units</span>
                                <span>{ selectedTotal.units }</span>
                            </p>
                            <p className="rollup-metric">
                                <span>Singles</span>
                                <span>{ selectedTotal.singles }</span>
                            </p>
                            <p className="rollup-metric">
                                <span>Sections</span>
                                <span>{ selectedTotal.sections }</span>
                            </p>
                            <p className="rollup-metric">
                                <span>Feet</span>
                                <span>{ selectedTotal.feet }</span>
                            </p>
                            <p className="rollup-metric">
                                <span>Weight</span>
                                <span>{ selectedTotal.weight }</span>
                            </p>
                            <p className="rollup-metric">
                                <span>Track</span>
                                <span>{ selectedTotal.track }</span>
                            </p>
                            <p className="rollup-metric">
                                <span>Hardware</span>
                                <span>{ selectedTotal.hardware }</span>
                            </p>
                        </div>                            
                    </div>
                </StickyActionBar>

                <RoutingRouteCreate
                    entityType={ entityType }
                    visible={ showInfoFlyout }
                    filters={ filterParams }
                    onSuccess={ onCreateSuccess }
                    onClose={ onCreateClose }
                    selectedAddresses={ selecteds } 
                />

    </React.Fragment>;

};
